// Snapshot-based undo history (§4.12). Documents are immutable (immer) so
// snapshots share structure; each entry remembers the cursor/selection before
// and after the edit.

import type { Score, Cursor, Selection } from '@simplenotation/core';

export interface CursorSnap {
  cursor: Cursor;
  selection: Selection | null;
}

export interface HistoryEntry {
  score: Score;
  label: string;
  before: CursorSnap;
  after: CursorSnap;
  coalesce?: string;
  time: number;
}

export interface History {
  entries: HistoryEntry[];
  /** Index of the entry whose score is current. */
  index: number;
  /** Index that matches the last saved document, or -1 if it was discarded. */
  savedIndex: number;
}

export const MAX_HISTORY = 500;

export function newHistory(score: Score, snap: CursorSnap): History {
  return { entries: [{ score, label: 'Open', before: snap, after: snap, time: 0 }], index: 0, savedIndex: 0 };
}

export function pushEntry(h: History, e: HistoryEntry, coalesceMs = 600): History {
  const cur = h.entries[h.index];
  if (e.coalesce && cur.coalesce === e.coalesce && e.time - cur.time < coalesceMs && h.index > 0) {
    const entries = h.entries.slice(0, h.index + 1);
    entries[h.index] = { ...e, before: cur.before };
    const savedIndex = h.savedIndex === h.index ? -1 : h.savedIndex > h.index ? -1 : h.savedIndex;
    return { entries, index: h.index, savedIndex };
  }
  let entries = h.entries.slice(0, h.index + 1);
  let savedIndex = h.savedIndex > h.index ? -1 : h.savedIndex;
  entries.push(e);
  let index = entries.length - 1;
  if (entries.length > MAX_HISTORY) {
    const drop = entries.length - MAX_HISTORY;
    entries = entries.slice(drop);
    index -= drop;
    savedIndex = savedIndex >= drop ? savedIndex - drop : -1;
  }
  return { entries, index, savedIndex };
}

export function unsavedCount(h: History): number {
  if (h.savedIndex < 0) return Math.max(1, h.index + 1);
  return Math.abs(h.index - h.savedIndex);
}

export function canUndo(h: History): boolean {
  return h.index > 0;
}

export function canRedo(h: History): boolean {
  return h.index < h.entries.length - 1;
}
